<template>
  <Layout title="" description="">
    <div class="container mx-auto p-6 max-w-5xl">
        <!-- Header Section -->
        <div class="mb-8 text-center">
        <div class="flex items-center justify-center gap-3 mb-4">
            <div class="p-3 rounded-full bg-green-100">
            <MessageSquare class="h-8 w-8 text-green-600" />
            </div>
            <h1 class="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-400 bg-clip-text text-transparent">
            How to Create and Manage Bookings via WhatsApp
            </h1>
        </div>
        <p class="text-gray-600 text-base sm:text-lg">
            Follow this simple 3-step guide to handle bookings using WhatsApp.
        </p>
        </div>

        <!-- Steps -->
        <div class="space-y-6 mb-8">
        <div
            v-for="(step, index) in steps"
            :key="step.title"
            class="border border-gray-200 rounded-xl shadow-sm bg-white overflow-hidden border-l-4 border-l-green-600"
        >
            <div class="p-5">
            <div class="flex items-start gap-4">
                <div class="flex items-center justify-center w-12 h-12 rounded-full bg-green-600 text-white font-bold text-xl flex-shrink-0">
                {{ index + 1 }}
                </div>
                <div class="flex-1">
                <h2 class="text-xl sm:text-2xl font-semibold flex items-center gap-2 text-gray-900">
                    <component :is="step.icon" class="h-6 w-6 text-green-600" />
                    {{ step.title }}
                    <span class="text-2xl">{{ step.emoji }}</span>
                </h2>
                <p class="mt-2 text-gray-600 text-sm sm:text-base">{{ step.description }}</p>
                </div>
            </div>
            </div>

            <!-- Example Message -->
            <div class="px-5 pb-5">
            <div class="flex justify-start mb-2">
                <div class="max-w-md">
                <div class="bg-green-600 text-white rounded-lg rounded-tl-none px-4 py-3 shadow-md">
                    <p class="text-sm leading-relaxed">{{ step.example }}</p>
                </div>
                <div class="text-xs text-gray-500 mt-1 px-2">
                    Example message
                </div>
                </div>
            </div>
            </div>
        </div>
        </div>

        <!-- Notes Section -->
        <div class="border-2 border-green-200 bg-green-50 rounded-xl shadow-sm p-5">
        <h3 class="flex items-center gap-2 text-xl font-semibold mb-4 text-gray-800">
            <AlertCircle class="h-5 w-5 text-green-600" />
            Important Notes
        </h3>
        <div class="space-y-3">
            <div class="flex gap-3 items-start">
            <div class="w-2 h-2 rounded-full bg-green-600 mt-2 flex-shrink-0" />
            <p class="text-sm text-gray-700">
                Please allow <strong>15 minutes</strong> for your booking to be processed.
            </p>
            </div>
            <div class="flex gap-3 items-start">
            <div class="w-2 h-2 rounded-full bg-green-600 mt-2 flex-shrink-0" />
            <p class="text-sm text-gray-700 flex items-center gap-2">
                If the booking is made between <strong>12 AM and 8 AM</strong>, please make a courtesy call.
                <Phone class="h-4 w-4 text-green-600" />
            </p>
            </div>
        </div>
        </div>

        <!-- Quick Tips -->
        <div class="border border-gray-200 bg-white rounded-xl shadow-sm p-5 mt-6">
        <h4 class="text-lg font-semibold mb-3 text-gray-800">Quick Tips</h4>
        <ul class="space-y-2">
            <li class="flex gap-2 items-start">
            <span class="text-green-600 font-bold">•</span>
            <p class="text-sm text-gray-600">
                Keep all booking communications in the same WhatsApp chat thread.
            </p>
            </li>
            <li class="flex gap-2 items-start">
            <span class="text-green-600 font-bold">•</span>
            <p class="text-sm text-gray-600">
                Include as much detail as possible in your initial message (pickup, destination, date, time, passengers, luggage).
            </p>
            </li>
            <li class="flex gap-2 items-start">
            <span class="text-green-600 font-bold">•</span>
            <p class="text-sm text-gray-600">
                You'll receive a confirmation message once your booking is confirmed.
            </p>
            </li>
        </ul>
        </div>
    </div>
  </Layout>
</template>

<script>
import { MessageSquare, Edit3, CheckCircle, AlertCircle, Phone } from "lucide-vue-next";
import Layout from '@/components/Layout.vue'

export default {
  name: "WhatsAppTutorial",
  components: { Layout,MessageSquare, Edit3, CheckCircle, AlertCircle, Phone },
  data() {
    return {
      steps: [
        {
          icon: MessageSquare,
          title: "Create a Booking",
          description: "Send a WhatsApp message with your trip details.",
          example: "AMD Transportation, Please book for 22/10/2025 9am from Changi Airport to Marina Bay Sands, Flight Number SQ315, Vesssel ABC Counter 16",
          emoji: "",
        },
        {
          icon: Edit3,
          title: "Update a Booking",
          description: "Need to change something? Just reply in the same chat — we'll update it for you.",
          example: "AMD Transportation, Please update pickup to Terminal 3.",
          emoji: "",
        },
        {
          icon: CheckCircle,
          title: "Confirm the Booking",
          description: "Once everything is correct, reply CONFIRM to lock in your booking.",
          example: "AMD Transportation, CONFIRM",
          emoji: "",
        },
      ],
    };
  },
};
</script>

<style scoped>
.bg-whatsapp {
  @apply bg-green-600;
}
.bg-whatsapp-light {
  @apply bg-green-50;
}
.text-whatsapp {
  @apply text-green-600;
}
</style>
